import { useState, useEffect } from "react";
import { Sparkles, Loader2, Check, Package } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Shop() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [inventory, setInventory] = useState({ owned_packs: [], gifts: [] });
  const [buying, setBuying] = useState(null);

  const loadInv = () => api.get("/shop/inventory").then((r) => setInventory(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/shop/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    if (user) loadInv();
  }, [user]);

  const buy = async (item) => {
    if (!user) { toast.error("Sign in to shop 💛"); return; }
    setBuying(item.id);
    try {
      const { data } = await api.post("/shop/checkout", { item_id: item.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setBuying(null); }
  };

  const packs = catalog.filter((i) => i.type === "pack");
  const gifts = catalog.filter((i) => i.type === "gift");
  const owns = (id) => inventory.owned_packs?.includes(id);
  const giftCount = (id) => inventory.gifts?.find((g) => g.id === id)?.count || 0;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex flex-col items-center text-center">
        <p className="font-mono text-xs uppercase tracking-widest text-copper flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Little Shop of Kindness</p>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-bronze mt-1">Sticker packs & gifts</h1>
        <div className="w-20 h-1 bg-gradient-to-r from-copper via-penny to-sage rounded-full my-2.5 opacity-90" />
        <p className="text-muted-foreground max-w-2xl">Unlock playful sticker packs and send heartfelt gifts in the Community Room. Every purchase helps keep HAPLAP running for people who need it.</p>
      </div>

      <h2 className="font-serif text-xl font-semibold text-bronze mt-8 mb-3 text-center">Sticker packs</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="shop-packs">
        {packs.map((item) => (
          <div key={item.id} data-testid={`shop-item-${item.id}`} className="bg-card rounded-2xl border border-border p-5 flex flex-col">
            <div className="text-4xl">{item.emoji}</div>
            <h3 className="font-serif font-semibold text-lg text-bronze mt-2">{item.name}</h3>
            <p className="text-sm text-foreground/70 mt-1 flex-1">{item.desc}</p>
            <div className="flex flex-wrap gap-1 mt-3">{item.stickers?.slice(0, 8).map((s, i) => <span key={i} className="text-lg">{s}</span>)}</div>
            {owns(item.id) ? (
              <Button disabled className="mt-4 rounded-full bg-sage/20 text-sage hover:bg-sage/20"><Check className="w-4 h-4 mr-1.5" /> Owned</Button>
            ) : (
              <Button data-testid={`buy-${item.id}`} onClick={() => buy(item)} disabled={buying === item.id} className="mt-4 rounded-full bg-copper hover:bg-copper/90 text-white">
                {buying === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : `Buy · $${item.price.toFixed(2)}`}
              </Button>
            )}
          </div>
        ))}
      </div>

      <h2 className="font-serif text-xl font-semibold text-bronze mt-10 mb-3 text-center">Send-a-gift</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="shop-gifts">
        {gifts.map((item) => (
          <div key={item.id} data-testid={`shop-item-${item.id}`} className="bg-card rounded-2xl border border-border p-4 flex flex-col items-center text-center">
            <div className="text-4xl">{item.emoji}</div>
            <h3 className="font-serif font-semibold text-bronze mt-2">{item.name}</h3>
            <p className="text-xs text-foreground/60 mt-1 flex-1">{item.desc}</p>
            {giftCount(item.id) > 0 && (
              <span className="text-[11px] text-sage font-medium mt-1 flex items-center gap-1"><Package className="w-3 h-3" /> {giftCount(item.id)} in inventory</span>
            )}
            <Button data-testid={`buy-${item.id}`} onClick={() => buy(item)} disabled={buying === item.id} className="mt-3 w-full rounded-full bg-penny hover:bg-penny/90 text-bronze">
              {buying === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : `$${item.price.toFixed(2)}`}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
